// Builds the request body for ordercapture/services/updateOrder — used after
// the Scheduling step books or resets a call, to mirror the appointment id
// onto order_header (see useOnboardingScheduling's syncOrderHeaderAppointment).
// Ports the old flow's captureOrderNewUpgrade → updateOrder round trip
// (screens/new-order.js / upgrade-order.js), which re-sends the SAME appt-id
// field it just booked or cleared (null) alongside the rest of the order.
//
// Read-modify-write, NOT a rebuild from Redux cart state: `currentOrder` is
// the response of GET ordercapture/services/getOrderDetails/:order_id (the
// whole order-capture-route.js router is mounted at /api/ordercapture/services
// in server.js — that route's own internal log message says
// /api/ordercapture/getOrderDetails, which is stale/wrong) — see
// order-capture-service.js's getOrderDetailsService, whose combinedOrderData
// already spreads every order_header column together with `lines` (the
// order_line rows) and `discounts` (the order_line_discount rows) in exactly
// the shape updateOrder expects them sent back — see fetchOrderLineByOrderId
// /fetchOrderLineDiscountByOrderId (db/dao/order-capture-dao.js), which wrap
// their rows as {lines: [...]} / {discounts: [...]} for this exact purpose.
//
// Rebuilding lines/discounts from the PurchaseLinkSlice cart instead (as an
// earlier version of this function did) is wrong: updateOrderDao's
// updateLine/updateDiscounts unconditionally DELETE and re-INSERT from
// whatever arrays are sent, and Redux never tracks coupons as
// order_line_discount rows at all (those are only written later, by
// cc/order/send-link's createOrder) — so a cart-rebuilt payload would silently
// wipe out any discount already applied by the time a rep reset a call after
// generating a payment link. Spreading the freshly-fetched `currentOrder`
// instead guarantees every other column/line/discount round-trips untouched.
//
// onboardingApptId/thryvSuccessServicesApptId: pass the appointment id
// string being set, or `null` to clear it — always both, since order_header
// itself has no per-location column and still only ever reflects the most
// recently scheduled/reset call across the whole combined order. locationThryvId
// additionally routes the same two ids onto that one location's own row in
// order_location_detail (see the read-modify-write below), which IS per-location
// and is what a multi-location order's downstream processing (cc-order-service.js's
// processMultiLocationPurchase) actually reads — order_header stays a
// whole-order legacy mirror, not the source of truth, same as before.
//
// Deliberately NOT a blind `...currentOrder` spread: a plain SELECT * also
// carries columns updateHeader never touches at all (keap_account, link_id,
// payment_uuid, click_count, ...), and captureOrderupdateSchema still
// validates every one of them regardless of whether updateHeader would've
// used it — so an irrelevant column with a DB-quirky type (e.g. keap_account
// coming back `null` where the schema only allows a real boolean or
// omitting the key) fails validation for a field this call was never trying
// to change. Explicitly listing exactly updateHeader's own SET fields below
// (kept in sync with db/dao/order-capture-dao.js's updateHeader — if that
// function's column list changes, mirror it here) sidesteps that whole
// category, not just today's specific two failures.
const HEADER_FIELDS = [
  'order_status',
  'order_completed_date',
  'order_flow',
  'thryv_id',
  'account_code',
  'eaid',
  'sales_rep_id',
  'on_behalf_of',
  'xp_code',
  'demo_lead_id',
  'contact_first_name',
  'contact_last_name',
  'contact_phone',
  'contact_mobile',
  'contact_email',
  'no_service',
  'industry_id',
  'industry_description',
  'is_invoice_billed',
  'update_to_autopay',
  'is_cohort',
  'on_boarding_fee',
  'country_code',
  'account_email',
  'company_name',
  'company_address1',
  'company_address2',
  'company_city',
  'company_state',
  'company_zipcode',
  'company_phone',
  'purchase_intent_1',
  'purchase_intent_2',
  'purchase_intent_3',
  'abn',
  'sensis_site',
]

export function buildUpdateOrderPayload(
  currentOrder,
  {
    orderId,
    onboardingApptId = null,
    thryvSuccessServicesApptId = null,
    locationThryvId = null,
  },
) {
  const payload = {}
  HEADER_FIELDS.forEach(field => {
    payload[field] = currentOrder?.[field] ?? null
  })

  // Read-modify-write for order_location_detail too, same reason as
  // lines/discounts above: updateOrderDao's updateLocationDetail deletes and
  // re-inserts ALL of this order's rows from whatever array is sent, so
  // omitting the other locations here would silently wipe their mc/keap
  // domain, seo proposal id, and appointment ids. Only the one location
  // whose call was just booked/reset gets its appt-id fields changed; every
  // other location's row (and every other field of this location's own row)
  // round-trips from currentOrder untouched.
  // currentOrder.location_details rows come straight from the DB (order_id,
  // created_date, create_oprid, ...) — re-shape to exactly what
  // captureOrderupdateSchema's location_details items accept before sending
  // any back, same principle as HEADER_FIELDS above.
  const existingLocationDetails = (currentOrder?.location_details ?? []).map(
    loc => ({
      location_thryv_id: loc.location_thryv_id,
      mc_domain: loc.mc_domain ?? null,
      keap_domain: loc.keap_domain ?? null,
      seo_proposal_id: loc.seo_proposal_id ?? null,
      onboarding_appt_id: loc.onboarding_appt_id ?? null,
      thryv_success_services_appt_id:
        loc.thryv_success_services_appt_id ?? null,
      keap_trail_id: loc.keap_trail_id ?? null,
    }),
  )
  const locationDetails = locationThryvId
    ? (() => {
        const found = existingLocationDetails.some(
          loc => loc.location_thryv_id === locationThryvId,
        )
        const updated = existingLocationDetails.map(loc =>
          loc.location_thryv_id === locationThryvId
            ? {
                ...loc,
                onboarding_appt_id: onboardingApptId,
                thryv_success_services_appt_id: thryvSuccessServicesApptId,
              }
            : loc,
        )
        return found
          ? updated
          : [
              ...existingLocationDetails,
              {
                location_thryv_id: locationThryvId,
                onboarding_appt_id: onboardingApptId,
                thryv_success_services_appt_id: thryvSuccessServicesApptId,
              },
            ]
      })()
    : existingLocationDetails

  return {
    ...payload,
    // order_version is a DECIMAL column, not VARCHAR — mysql2 returns it as
    // a number (5, not '5.0'), which fails captureOrderupdateSchema's
    // string-only .valid('4.0', '5.0') check. Every order this app creates
    // is order_version '5.0' (see buildCreateOrderPayload) and scheduling
    // could never have legitimately changed that, so re-affirm the constant
    // rather than coerce the fetched number.
    order_version: '5.0',
    lines: currentOrder?.lines ?? [],
    discounts: currentOrder?.discounts ?? [],
    location_details: locationDetails,
    order_id: orderId,
    onboarding_appt_id: onboardingApptId,
    thryv_success_services_appt_id: thryvSuccessServicesApptId,
  }
}
